import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { MicState } from '@harmony/live-client'
import { Box, Icon, MotionBox, Stack, Text } from '@ui'
import { Dropdown } from 'ui/components/Dropdown/Dropdown'

import { isSinkIdSupported } from './liveUtils'

type MicInfoPopupProps = {
    micState: MicState
    /** Callback when user selects a mic device */
    onDeviceSelect?: (device: MediaDeviceInfo) => void
    /** Callback when user selects an output device */
    onOutputDeviceSelect?: (device: MediaDeviceInfo) => void
}

export const MicInfoPopup = ({
    micState,
    onDeviceSelect,
    onOutputDeviceSelect,
}: MicInfoPopupProps) => {
    const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([])
    const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string>('')

    // Fetch output devices (only if setSinkId is supported)
    useEffect(() => {
        if (!isSinkIdSupported) {
            return
        }

        const fetchOutputDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices()
                const audioOutputs = devices.filter((d) => d.kind === 'audiooutput')
                setOutputDevices(audioOutputs)
                // Set default device or fallback if current device no longer exists
                setSelectedOutputDeviceId((prev) => {
                    const stillExists = audioOutputs.some((d) => d.deviceId === prev)
                    if (prev && stillExists) {
                        return prev
                    }
                    return audioOutputs.length > 0 ? audioOutputs[0].deviceId : ''
                })
            } catch (error) {
                console.error('Failed to enumerate output devices:', error)
            }
        }
        fetchOutputDevices()

        // Listen for device changes
        navigator.mediaDevices.addEventListener('devicechange', fetchOutputDevices)
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', fetchOutputDevices)
        }
    }, [])

    // Mic device options for dropdown
    const micOptions = useMemo(
        () =>
            (micState.devices ?? []).map((device, index) => ({
                label: device.label || `Microphone ${index + 1}`,
                value: device.deviceId,
            })),
        [micState.devices],
    )

    // Output device options for dropdown
    const outputOptions = useMemo(
        () =>
            outputDevices.map((device, index) => ({
                label: device.label || `Speaker ${index + 1}`,
                value: device.deviceId,
            })),
        [outputDevices],
    )

    const handleMicChange = useCallback(
        (deviceId: string) => {
            const device = micState.devices?.find((d) => d.deviceId === deviceId)
            if (device && onDeviceSelect) {
                onDeviceSelect(device)
            }
        },
        [micState.devices, onDeviceSelect],
    )

    const handleOutputChange = useCallback(
        (deviceId: string) => {
            setSelectedOutputDeviceId(deviceId)
            const device = outputDevices.find((d) => d.deviceId === deviceId)
            if (device && onOutputDeviceSelect) {
                onOutputDeviceSelect(device)
            }
        },
        [outputDevices, onOutputDeviceSelect],
    )

    // Show row if there's at least one device, but only as dropdown if >1
    const showMicRow = micOptions.length >= 1
    const canChangeMic = micOptions.length > 1
    const showOutputSelector = isSinkIdSupported

    return (
        <Box>
            <MotionBox initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Stack gap="x1.5">
                    {/* Error if any */}
                    {micState.error && (
                        <Box
                            background="level2"
                            paddingY="xs"
                            paddingX="x1.5"
                            borderRadius="sm"
                            height="x4"
                            justifyContent="center"
                        >
                            <Text size="sm" color="negative">
                                Error: {micState.error.message}
                            </Text>
                        </Box>
                    )}

                    {/* Microphone selector */}
                    {showMicRow && (
                        <Stack horizontal gap="sm" alignItems="center">
                            <Box
                                centerContent
                                shrink
                                rounded
                                background="level2"
                                minWidth="x5"
                                minHeight="x5"
                            >
                                <Icon type="microphone" color="gray1" size="square_xs" />
                            </Box>
                            {canChangeMic ? (
                                <Box grow overflow="hidden" padding="2">
                                    <Dropdown
                                        height="x5"
                                        maxWidth="100%"
                                        options={micOptions}
                                        value={micState.activeDevice?.deviceId ?? ''}
                                        background="level2"
                                        onChange={handleMicChange}
                                    />
                                </Box>
                            ) : (
                                <Text truncate size="sm" color="gray1">
                                    {micOptions[0]?.label ?? 'Default microphone'}
                                </Text>
                            )}
                        </Stack>
                    )}

                    {/* Speaker selector */}
                    {showOutputSelector && (
                        <Stack horizontal gap="sm" alignItems="center" overflow="hidden">
                            <Box
                                centerContent
                                rounded
                                background="level2"
                                minWidth="x5"
                                minHeight="x5"
                            >
                                <Icon type="speaker" color="gray1" size="square_xs" />
                            </Box>
                            <Box grow overflow="hidden" padding="2">
                                <Dropdown
                                    height="x5"
                                    options={outputOptions}
                                    value={selectedOutputDeviceId}
                                    background="level2"
                                    maxWidth="100%"
                                    onChange={handleOutputChange}
                                />
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </MotionBox>
        </Box>
    )
}
